import { useState, useEffect, useMemo } from 'react';
import {
  balanceService,
  BalanceRow,
  BalanceSummary,
  BalanceFilters,
} from '../../services/balance.service';
import { catalogService, CatalogItem } from '../../services/catalog.service';

export function BalancesPage() {
  const [filters, setFilters] = useState<BalanceFilters>({
    only_with_balance: false,
  });

  const [balances, setBalances] = useState<BalanceRow[]>([]);
  const [summary, setSummary] = useState<BalanceSummary | null>(null);

  const [currencyTypes, setCurrencyTypes] = useState<CatalogItem[]>([]);
  const [departments, setDepartments] = useState<CatalogItem[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [groupByOwner, setGroupByOwner] = useState(true);

  useEffect(() => {
    loadCatalogs();
  }, []);

  useEffect(() => {
    loadData();
  }, [
    filters.owner_type,
    filters.currency_type_id,
    filters.department_id,
    filters.search,
    filters.only_with_balance,
  ]);

  const loadCatalogs = async () => {
    try {
      const [curr, deps] = await Promise.all([
        catalogService.list('currency_types'),
        catalogService.list('departments'),
      ]);
      setCurrencyTypes(curr);
      setDepartments(deps);
    } catch (err) {
      console.error(err);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [rows, sum] = await Promise.all([
        balanceService.listBalances(filters),
        balanceService.getSummary(filters),
      ]);
      setBalances(rows);
      setSummary(sum);
    } catch (err: any) {
      setError(err.message || 'Error al cargar saldos');
    } finally {
      setIsLoading(false);
    }
  };

  const updateFilter = (key: keyof BalanceFilters, value: any) => {
    setFilters((prev: BalanceFilters) => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const clearFilters = () => {
    setFilters({ only_with_balance: false });
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.owner_type) count++;
    if (filters.currency_type_id) count++;
    if (filters.department_id) count++;
    if (filters.search) count++;
    if (filters.only_with_balance) count++;
    return count;
  }, [filters]);

  const groupedByOwner = useMemo(() => {
    const map = new Map<string, {
      owner_id: string;
      owner_type: 'user' | 'department' | 'location';
      owner_name: string;
      department_name?: string;
      wallets: BalanceRow[];
      totalByCurrency: Record<string, number>;
    }>();

    for (const row of balances) {
      const key = `${row.owner_type}:${row.owner_id}`;
      if (!map.has(key)) {
        map.set(key, {
          owner_id: row.owner_id,
          owner_type: row.owner_type,
          owner_name: row.owner_name,
          department_name: row.owner_department_name,
          wallets: [],
          totalByCurrency: {},
        });
      }
      const entry = map.get(key)!;
      entry.wallets.push(row);
      entry.totalByCurrency[row.currency_type_id] =
        (entry.totalByCurrency[row.currency_type_id] || 0) + row.balance;
    }

    return Array.from(map.values()).sort((a, b) => {
      const order = { user: 0, department: 1, location: 2 };
      if (order[a.owner_type] !== order[b.owner_type]) {
        return order[a.owner_type] - order[b.owner_type];
      }
      return a.owner_name.localeCompare(b.owner_name);
    });
  }, [balances]);

  const exportCSV = () => {
    const headers = [
      'Tipo', 'Nombre', 'Departamento', 'Moneda',
      'Saldo', 'Última actualización',
    ];
    const rows = balances.map(r => [
      r.owner_type === 'user' ? 'Usuario' :
      r.owner_type === 'department' ? 'Departamento' : 'Ubicación',
      r.owner_name,
      r.owner_department_name || '',
      r.currency_name,
      r.balance.toFixed(2),
      new Date(r.updated_at).toLocaleString('es-MX'),
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `saldos_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getOwnerTypeLabel = (type: string) => {
    const labels: Record<string, { label: string; icon: string }> = {
      user: { label: 'Usuario', icon: '👤' },
      department: { label: 'Departamento', icon: '🏢' },
      location: { label: 'Ubicación', icon: '📍' },
    };
    return labels[type] || { label: type, icon: '•' };
  };

  return (
    <div className="page-container">
      {/* Encabezado */}
      <div className="page-header-bar">
        <div>
          <h1>Saldos</h1>
          <p className="subtitle">
            Vista consolidada de wallets de usuarios, departamentos y ubicaciones físicas
          </p>
        </div>
        <div className="actions">
          <button onClick={loadData} className="btn btn-ghost">🔄 Actualizar</button>
          <button onClick={exportCSV} className="btn btn-primary" disabled={balances.length === 0}>
            📥 Exportar CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="info-panel error">
          <span className="info-panel-icon">❌</span>
          <div className="info-panel-body">
            <p className="info-panel-text">{error}</p>
          </div>
        </div>
      )}

      {/* Resumen consolidado */}
      {summary && summary.currencies.length > 0 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3>📊 Resumen Consolidado</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '16px',
            marginTop: '16px',
          }}>
            {summary.currencies.map((curr) => (
              <div
                key={curr.currency_type_id}
                style={{
                  padding: '18px',
                  background: 'var(--bg)',
                  borderRadius: 'var(--radius)',
                  borderLeft: '4px solid var(--primary)',
                }}
              >
                <div style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '6px' }}>
                  {curr.currency_name}
                </div>
                <div style={{ fontSize: '26px', fontWeight: 700, color: 'var(--primary)' }}>
                  {formatCurrency(curr.total)}
                </div>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  marginTop: '10px',
                  fontSize: '11px',
                  color: 'var(--gray)',
                  flexWrap: 'wrap',
                }}>
                  <span>👤 {formatCurrency(curr.by_owner_type.user)}</span>
                  <span>🏢 {formatCurrency(curr.by_owner_type.department)}</span>
                  <span>📍 {formatCurrency(curr.by_owner_type.location)}</span>
                </div>
              </div>
            ))}
          </div>

          <div style={{
            display: 'flex',
            gap: '20px',
            marginTop: '16px',
            paddingTop: '12px',
            borderTop: '1px dashed var(--light-gray)',
            fontSize: '12px',
            color: 'var(--gray)',
            flexWrap: 'wrap',
          }}>
            <span><strong>{summary.counts.wallets}</strong> wallets</span>
            <span><strong>{summary.counts.users}</strong> usuarios</span>
            <span><strong>{summary.counts.departments}</strong> departamentos</span>
            <span><strong>{summary.counts.locations}</strong> ubicaciones</span>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="toolbar">
        <div className="toolbar-search">
          <span className="toolbar-search-icon">🔍</span>
          <input
            type="text"
            className="form-input"
            placeholder="Buscar por nombre..."
            value={filters.search || ''}
            onChange={(e) => updateFilter('search', e.target.value)}
          />
        </div>

        <div className="toolbar-filters">
          <select
            className="form-select"
            value={filters.owner_type || ''}
            onChange={(e) => updateFilter('owner_type', e.target.value)}
          >
            <option value="">Todos los tipos</option>
            <option value="user">👤 Usuarios</option>
            <option value="department">🏢 Departamentos</option>
            <option value="location">📍 Ubicaciones</option>
          </select>

          <select
            className="form-select"
            value={filters.currency_type_id || ''}
            onChange={(e) => updateFilter('currency_type_id', e.target.value)}
          >
            <option value="">Todas las monedas</option>
            {currencyTypes.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <select
            className="form-select"
            value={filters.department_id || ''}
            onChange={(e) => updateFilter('department_id', e.target.value)}
          >
            <option value="">Todos los departamentos</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>

          <label className="checkbox-field" style={{ padding: '8px 12px' }}>
            <input
              type="checkbox"
              checked={!!filters.only_with_balance}
              onChange={(e) => updateFilter('only_with_balance', e.target.checked)}
            />
            <span style={{ fontSize: '13px' }}>Solo con saldo</span>
          </label>

          {activeFiltersCount > 0 && (
            <>
              <span className="filter-badge">
                {activeFiltersCount} filtro{activeFiltersCount > 1 ? 's' : ''}
              </span>
              <button onClick={clearFilters} className="btn btn-ghost btn-sm">
                Limpiar
              </button>
            </>
          )}
        </div>

        <div className="toolbar-actions">
          <button
            onClick={() => setGroupByOwner(!groupByOwner)}
            className="btn btn-ghost btn-sm"
            title={groupByOwner ? 'Cambiar a vista plana' : 'Cambiar a vista agrupada'}
          >
            {groupByOwner ? '📋 Vista plana' : '📁 Agrupar por owner'}
          </button>
        </div>
      </div>

      {/* Contenido */}
      {isLoading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p className="loading-text">Cargando saldos...</p>
        </div>
      ) : balances.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">💼</div>
          <h3 className="empty-state-title">Sin resultados</h3>
          <p className="empty-state-text">
            {activeFiltersCount > 0
              ? 'No se encontraron wallets con los filtros aplicados.'
              : 'Aún no hay wallets con saldo en el sistema.'}
          </p>
        </div>
      ) : groupByOwner ? (
        <div className="cards-grid-2">
          {groupedByOwner.map(owner => {
            const typeInfo = getOwnerTypeLabel(owner.owner_type);
            return (
              <div key={`${owner.owner_type}:${owner.owner_id}`} className="card">
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '10px',
                  marginBottom: '14px',
                }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px',
                    }}>
                      <span style={{ fontSize: '20px' }}>{typeInfo.icon}</span>
                      <h3 style={{
                        fontSize: '15px',
                        fontWeight: 700,
                        color: '#0f172a',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {owner.owner_name}
                      </h3>
                    </div>
                    <p style={{ fontSize: '12px', color: 'var(--gray)', margin: 0 }}>
                      {typeInfo.label}
                      {owner.department_name && ` • ${owner.department_name}`}
                    </p>
                  </div>
                  <span className="badge badge-neutral">{owner.wallets.length} 💼</span>
                </div>

                <div>
                  {owner.wallets.map(wallet => (
                    <div
                      key={wallet.wallet_id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: '1px dashed var(--light-gray)',
                      }}
                    >
                      <span style={{ fontSize: '13px', color: 'var(--gray)' }}>
                        {wallet.currency_name}
                      </span>
                      <span style={{
                        fontSize: '15px',
                        fontWeight: 700,
                        color: wallet.balance !== 0 ? 'var(--primary)' : '#94a3b8',
                      }}>
                        {formatCurrency(wallet.balance)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Nombre</th>
                <th>Departamento</th>
                <th>Moneda</th>
                <th style={{ textAlign: 'right' }}>Saldo</th>
                <th>Actualizado</th>
              </tr>
            </thead>
            <tbody>
              {balances.map(row => {
                const typeInfo = getOwnerTypeLabel(row.owner_type);
                return (
                  <tr key={row.wallet_id}>
                    <td>
                      <span className="badge badge-neutral">
                        {typeInfo.icon} {typeInfo.label}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{row.owner_name}</td>
                    <td style={{ color: 'var(--gray)', fontSize: '13px' }}>
                      {row.owner_department_name || '—'}
                    </td>
                    <td>{row.currency_name}</td>
                    <td style={{
                      textAlign: 'right',
                      fontWeight: 700,
                      color: row.balance !== 0 ? 'var(--primary)' : '#94a3b8',
                    }}>
                      {formatCurrency(row.balance)}
                    </td>
                    <td style={{ fontSize: '12px', color: 'var(--gray)' }}>
                      {new Date(row.updated_at).toLocaleString('es-MX', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}